import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal
} from '@angular/core';
import {
  FaDuotoneIconComponent,
  FaIconComponent
} from '@fortawesome/angular-fontawesome';
import {
  faAngleDown,
  faAngleUp,
  faArrowLeft,
  faBackwardStep,
  faListMusic,
  faRotateLeft,
  faRotateRight
} from '@fortawesome/pro-regular-svg-icons';
import { KexpDialogsService } from '../../../../services/dialogs.service';
import {
  InstanceManagerService, PlayerControlState,
  SiPlayerController,
  SplixerLayoutControlService,
  StationManagerService,
  TimelineService
} from '@splixer/player';
import { KexpMiniPlaylistComponent } from '../../mini-playlist/mini-playlist.component';
import { KexpMiniTrackInfoComponent } from '../../track-info/mini-track-info.component';
import { KexpMixAndSegmentComponent } from '../../current-mix/mix-and-segment.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { KexpMediaScrubBarComponent } from '../../player/media-scrub-bar.component';
import { SecondsToMinutesPipe } from '@hidat/ng-common';
import { Router } from '@angular/router';
import { faCirclePlay } from '@fortawesome/pro-duotone-svg-icons/faCirclePlay';
import { faCirclePause } from '@fortawesome/pro-duotone-svg-icons/faCirclePause';
import { faForwardStep } from '@fortawesome/sharp-regular-svg-icons';
import { KexpSteeringWheelComponent } from '../../steering-wheel/steering-wheel.component';
import { SteeringMode } from '@splixer/common';
import { nString } from '@hidat/huijs-common';

enum ExpandedViews {
  None,
  MixInfo,
  SteeringWheel,
  Playlist,
  TrackInfo,
}

/**
 * Combination now playing with simple player
 * Kind of big, but it's all related....
 */
@Component({
  selector: 'kexp-now-player',
  templateUrl: 'now-player.component.html',
  styleUrl: 'now-player.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FaIconComponent,
    KexpMiniPlaylistComponent,
    KexpMiniTrackInfoComponent,
    KexpMixAndSegmentComponent,
    FaDuotoneIconComponent,
    NgTemplateOutlet,
    KexpMediaScrubBarComponent,
    SecondsToMinutesPipe,
    KexpSteeringWheelComponent,
    NgClass
  ]
})
export class NowPlayerComponent extends SiPlayerController {
  protected timeline = inject(TimelineService);
  protected instance = inject(InstanceManagerService);
  protected station = inject(StationManagerService);
  protected router = inject(Router);
  protected dialogService = inject(KexpDialogsService);
  protected layout = inject(SplixerLayoutControlService);

  protected readonly faArrowLeft = faArrowLeft;
  protected playIcon = faCirclePlay;
  protected pauseIcon = faCirclePause;
  protected skipIcon = faForwardStep;
  protected skipBackIcon = faBackwardStep;
  protected backIcon = faRotateLeft;
  protected forwardIcon = faRotateRight;
  protected readonly faExpandIcon = faAngleUp;
  protected readonly faContractIcon = faAngleDown;
  protected readonly faBackArrowIcon = faArrowLeft;
  protected playlistToggleIcon = signal(faListMusic);

  protected readonly ExpandedViews = ExpandedViews;
  protected defaultView = ExpandedViews.None;
  protected expanded = signal(false);
  protected expandedView = signal(this.defaultView);
  protected readonly SteeringMode = SteeringMode;

  protected bottomSheetKlasses = signal<string[] | undefined>(undefined);
  protected mixInfoNavKlasses = signal<string[] | undefined>(undefined);
  protected steeringWheelNavKlasses = signal<string[] | undefined>(undefined);
  protected playlistNavKlasses = signal<string[] | undefined>(undefined);

  protected playerVisible = computed(() => {
    return this.playbackService.firstPlayStarted();
  });

  protected playlistHasItems = computed(() => {
    const plItems = this.timeline.currentTimelineItems();
    return plItems && plItems.length > 0;
  });

  protected npImageUrl = computed(() => {
    const np = this.timeline.nowPlayingItem();
    if (np) {
      return this.station.getTimelineImageUrl(np);
    }
    return undefined;
  });

  protected npImageLabel = computed(() => {
    const np = this.timeline.nowPlayingItem();
    if (np) {
      return this.station.getTimelineImageLabel(np, true);
    }
    return '';
  });

  protected skipButtonTitle = computed(() => {
    let skipName: string | undefined;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const mi = this.npMediaItemIndex(); // without this we don't get change detection...
    if (this.mediaPlaying) {
      if (this.mediaPlaying.lastInItem) {
        skipName = 'Next show';
      } else {
        skipName = 'Next hour';
      }
    }
    return skipName;
  });

  protected playPauseButtonLabel = computed(() => {
    const cs = this.controlState();
    if (cs === PlayerControlState.Play) {
      return 'Play Audio'
    } else if (cs === PlayerControlState.Pause) {
      return 'Pause Audio'
    }
    return '';
  })

  protected skipPreviousTitle = computed(() => {
    let skipName: string | undefined;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const mi = this.npMediaItemIndex(); // without this we don't get change detection...
    if (this.mediaPlaying) {
      if (this.mediaPlaying.firstInItem) {
        skipName = 'Start over';
      } else {
        skipName = 'Prev hour';
      }
    }
    return skipName;
  });

  protected showTrackInfoEvent = (event?: Event) => {
    if (event) {
      event.stopPropagation();
    }
    const np = this.timeline.nowPlayingItem();
    if (np) {
      this.dialogService.showTrackInfoDialog(np, this.eventSource);
    }
  };

  protected toggleExpandedEvent = (event: Event) => {
    if (event) {
      event.stopPropagation();
    }
    if (this.expanded()) {
      this.hideTabContent(null);
      this.expanded.set(false);
      this.eventTracker.trackEventWithSource(
        'player_contract',
        this.eventSource
      );
    } else {
      this.expanded.set(true);
      this.eventTracker.trackEventWithSource('player_expand', this.eventSource);
      if (this.ranOutOfContent()) {
        this.toggleSteeringWheel('out_of_content');
      } else {
        this.expandedView.set(this.defaultView);
      }
    }
  };

  protected fullPlaylistEvent = () => {
    this.router.navigate(['/np/playlist']);
    this.eventTracker.showPlaylist(this.eventSource);
    this.hideTabContent('playlist_nav');
  };

  protected scrubBarClicked = (mediaItemIndex: number) => {
    this.gotoSegment(mediaItemIndex);
  };

  protected hideTabContent(eventSource: nString) {
    if (eventSource !== null) {
      this.eventTracker.trackEventWithSource(
        'player_contract',
        eventSource ?? this.eventSource
      );
    }
    this.expandedView.set(ExpandedViews.None);
    this.hideBottomSheet();
  }

  protected showSteeringWheelEvent = () => {
    this.toggleSteeringWheel();
  };

  protected toggleSteeringWheel(eventSource?: nString) {
    if (this.expandedView() === ExpandedViews.SteeringWheel) {
      this.hideTabContent(eventSource);
    } else {
      if (eventSource !== null) {
        this.eventTracker.trackEventWithSource(
          'steering_wheel_select',
          eventSource ?? this.eventSource
        );
      }
      this.expandedView.set(ExpandedViews.SteeringWheel);
      this.playlistNavKlasses.set(undefined);
      this.steeringWheelNavKlasses.set(['border-b-1']);
      this.mixInfoNavKlasses.set(undefined);
      this.showBottomSheet();
    }
  }

  protected showPlaylistEvent = () => {
    this.toggleMiniPlaylist();
  };

  protected toggleMiniPlaylist() {
    if (this.expandedView() === ExpandedViews.Playlist) {
      this.hideTabContent('tab_toggle');
    } else {
      this.eventTracker.trackEventWithSource(
        'recently_played_select',
        this.eventSource
      );
      this.expandedView.set(ExpandedViews.Playlist);
      this.playlistNavKlasses.set(['border-b-1']);
      this.steeringWheelNavKlasses.set(undefined);
      this.mixInfoNavKlasses.set(undefined);
      this.showBottomSheet();
    }
  }

  protected showMixInfoEvent = () => {
    this.toggleMixInfo();
  };

  protected toggleMixInfo() {
    if (this.expandedView() === ExpandedViews.MixInfo) {
      this.hideTabContent('tab_toggle');
    } else {
      this.eventTracker.trackEventWithSource(
        'mix_info_select',
        this.eventSource
      );
      this.expandedView.set(ExpandedViews.MixInfo);
      this.mixInfoNavKlasses.set(['border-b-1']);
      this.steeringWheelNavKlasses.set(undefined);
      this.playlistNavKlasses.set(undefined);
      this.showBottomSheet();
    }
  }

  protected showBottomSheet() {
    this.bottomSheetKlasses.set(['active']);
  }

  protected hideBottomSheet() {
    this.bottomSheetKlasses.set(undefined);
    this.playlistNavKlasses.set(undefined);
    this.steeringWheelNavKlasses.set(undefined);
    this.mixInfoNavKlasses.set(undefined);
  }

  protected steeringPathTaken = () => {
    this.hideTabContent('steering_wheel');
  };

  protected override onOutOfContent() {
    if (this.expanded()) {
      this.toggleSteeringWheel('out_of_content');
    }
  }
}
